<script setup>
import { Head } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import VariantPageWrapper from '@/Components/VariantPageWrapper.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import LeadFunnel from '@/Components/LeadFunnel.vue';
import { useDesignSet } from '@/composables/useDesignSet';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    spotImages: Object,
    // AGENT.md anti-detection rule #7: per-domain author persona,
    // hydrated by StaticPageController via AuthorResolver.
    missionAuthor: { type: Object, default: null },
});

const { isClassic } = useDesignSet();

const accent = props.domain?.settings?.brand_accent
    || props.domain?.settings?.brand_color
    || '#ea580c';

const serviceLabel = props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik';
const isBrandDirect = checkBrandDirect(props.domain);
const brandName = props.domain?.settings?.brand_name || props.domain?.name || 'Portal';

const stats = [
    { value: 'Kostenlos', label: 'für Hauseigentümer' },
    { value: 'Bundesweit', label: 'regionale Fachbetriebe' },
    { value: '100 %', label: 'Kostenloser Service' },
    { value: 'Bis zu 3', label: 'Angebote möglich' },
];

const values = [
    { title: 'Kostenlos', desc: 'Die Anfrage ist für Sie kostenlos und unverbindlich. Wir finanzieren uns über eine Vermittlungsvergütung der Fachbetriebe.' },
    { title: 'Transparent', desc: 'Keine versteckten Kosten, keine Tricks. Sie erhalten echte Vergleichsangebote von qualifizierten Fachbetrieben.' },
    { title: 'Regional', desc: 'Wir kennen die lokalen Fördermittel und regionalen Anforderungen und verbinden Sie mit Betrieben vor Ort.' },
];
</script>

<template>
    <Head :title="isBrandDirect ? `Über ${brandName} - Ihr Fachbetrieb für ${serviceLabel}` : 'Über uns - Vergleichsportal für PV und Wärme'">
        <meta name="description" :content="isBrandDirect ? `${brandName}: Beratung, Planung und Installation von ${serviceLabel} aus einer Hand. Persönlich, zuverlässig, regional.` : `Wir verbinden Hauseigentümer mit qualifizierten Fachbetrieben für ${serviceLabel} in ganz Deutschland - kostenlos und unverbindlich.`" />
        <meta property="og:title" :content="isBrandDirect ? `Über ${brandName}` : 'Über uns - Photovoltaik-Vergleichsportal für ganz Deutschland'" />
        <meta property="og:type" content="website" />
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&display=swap" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="about" heroMode :hideChrome="!!domain?.settings?.design_variant">
        <VariantPageWrapper :domain="domain">
            <div class="pvhx-about" :style="{ '--pvhx-accent': accent }">
                <!-- Hero -->
                <section class="pvhx-about-hero">
                    <div class="pvhx-wrap">
                        <p class="pvhx-eyebrow">Über uns</p>
                        <h1 v-if="isBrandDirect" class="pvhx-about-h1">Über {{ brandName }}.</h1>
                        <h1 v-else class="pvhx-about-h1">PV und Wärme in einer Planung.</h1>
                        <p v-if="isBrandDirect" class="pvhx-lead">{{ brandName }} ist Ihr Fachbetrieb für {{ serviceLabel }} - Beratung, Planung und Installation aus einer Hand. Persönlich, zuverlässig, regional.</p>
                        <p v-else class="pvhx-lead">Als Vergleichsportal verbinden wir Hauseigentümer mit Fachbetrieben für Photovoltaik plus Wärmepumpe - kostenlos und unverbindlich.</p>
                    </div>
                </section>

                <!-- Mission -->
                <section class="pvhx-section">
                    <div class="pvhx-wrap">
                        <div class="pvhx-mission">
                            <div
                                class="pvhx-mission__media"
                                role="img"
                                aria-label="Bild unserer Mission"
                                :style="spotImages?.about_mission ? { backgroundImage: `url(${spotImages.about_mission})` } : {}"
                            ></div>
                            <div class="pvhx-mission__content">
                                <p class="pvhx-eyebrow">Unsere Mission</p>
                                <h2 v-if="isBrandDirect" class="pvhx-h2">Unser Anspruch.</h2>
                                <h2 v-else class="pvhx-h2">Warum wir tun, was wir tun.</h2>
                                <template v-if="isBrandDirect">
                                    <p class="pvhx-p">Bei {{ brandName }} steht echter persönlicher Service an erster Stelle. Wir beraten, planen und installieren Ihre Anlage - alles aus einer Hand, von echten Menschen statt Call-Centern.</p>
                                    <p class="pvhx-p">Unser Ziel ist es, Hausbesitzern den Umstieg auf erneuerbare Energien so einfach und transparent wie möglich zu machen.</p>
                                </template>
                                <template v-else>
                                    <p class="pvhx-p">Strom und Wärme gehören zusammen. Unser Ziel ist eine gemeinsame Auslegung als Schätzung: PV-kWp, Wärmepumpen-kW, Speicher und grober Investrahmen.</p>
                                    <p class="pvhx-p">Die Werte sind Richtwerte. Verbindliche Preise nennt der Fachbetrieb. Wir finanzieren uns über eine Vermittlungsvergütung der Fachbetriebe.</p>
                                </template>
                                <div class="pvhx-mission__trust">
                                    <span class="pvhx-trust-item">{{ isBrandDirect ? 'Persönlicher Service' : 'Kostenlos für Sie' }}</span>
                                    <span class="pvhx-trust-item">Seit 2024 aktiv</span>
                                    <span class="pvhx-trust-item">Bundesweit</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Stats -->
                <section class="pvhx-section pvhx-section--tight">
                    <div class="pvhx-wrap">
                        <h2 class="pvhx-h2 pvhx-h2--center">Was Sie erwarten können</h2>
                        <div class="pvhx-stats">
                            <div v-for="stat in stats" :key="stat.label" class="pvhx-stat">
                                <div class="pvhx-stat__value">{{ stat.value }}</div>
                                <div class="pvhx-stat__label">{{ stat.label }}</div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Values -->
                <section class="pvhx-section pvhx-section--tight">
                    <div class="pvhx-wrap">
                        <h2 class="pvhx-h2 pvhx-h2--center">Was uns auszeichnet</h2>
                        <div class="pvhx-values">
                            <div v-for="(item, idx) in values" :key="item.title" class="pvhx-value">
                                <div class="pvhx-value__num">{{ idx + 1 }}</div>
                                <h3 class="pvhx-h3">{{ item.title }}</h3>
                                <p class="pvhx-p">{{ item.desc }}</p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Regional -->
                <section class="pvhx-section pvhx-section--tight">
                    <div class="pvhx-wrap">
                        <div class="pvhx-regional">
                            <div class="pvhx-regional__content">
                                <h2 class="pvhx-h2">Bundesweit für Sie da.</h2>
                                <p class="pvhx-p">Ob Hamburg, Berlin, München oder Köln - unser Netzwerk umfasst regionale Fachbetriebe in zahlreichen Städten. Wir kennen die lokalen Fördermittel und regionalen Anforderungen.</p>
                                <div class="pvhx-callout">
                                    <p class="pvhx-callout__label">Wussten Sie schon?</p>
                                    <p class="pvhx-callout__text">Ob Photovoltaik auf dem Dach oder Wärmepumpe im Bestand: Mit guter Planung und dem richtigen Fachbetrieb lassen sich überall in Deutschland wirtschaftliche und langlebige Energielösungen umsetzen.</p>
                                </div>
                            </div>
                            <div
                                class="pvhx-regional__media"
                                role="img"
                                aria-label="Bundesweites Netzwerk"
                                :style="spotImages?.about_regional ? { backgroundImage: `url(${spotImages.about_regional})` } : {}"
                            ></div>
                        </div>
                    </div>
                </section>

                <!-- AGENT.md rule #7: per-domain author persona. -->
                <section v-if="missionAuthor" class="pvhx-section pvhx-section--tight pvhx-author-section" aria-label="Über die redaktionelle Verantwortung">
                    <div class="pvhx-wrap pvhx-wrap--narrow">
                        <p class="pvhx-eyebrow">Redaktionelle Verantwortung</p>
                        <h2 class="pvhx-h2">
                            Hinter dieser Seite steht <span class="pvhx-author-accent">{{ missionAuthor.name }}</span>.
                        </h2>
                        <p v-if="missionAuthor.headline" class="pvhx-author-headline">{{ missionAuthor.headline }}</p>
                        <div class="pvhx-author-grid">
                            <div class="pvhx-author-photo" aria-hidden="true">
                                <div class="pvhx-author-photo__fallback">{{ (missionAuthor.vorname || missionAuthor.name).charAt(0) }}{{ (missionAuthor.nachname || '').charAt(0) }}</div>
                            </div>
                            <div class="pvhx-author-body">
                                <p v-for="(para, i) in (missionAuthor.bio_long || '').split('\n\n')" :key="i" class="pvhx-p">{{ para }}</p>
                            </div>
                        </div>
                        <div class="pvhx-author-meta">
                            <div v-if="missionAuthor.credentials && missionAuthor.credentials.length" class="pvhx-author-creds">
                                <p class="pvhx-author-creds__label">Qualifikationen</p>
                                <ul class="pvhx-author-creds__list">
                                    <li v-for="(c, i) in missionAuthor.credentials" :key="i">{{ c }}</li>
                                </ul>
                            </div>
                            <div class="pvhx-author-facts">
                                <div v-if="missionAuthor.years_experience" class="pvhx-author-fact">
                                    <span class="pvhx-author-fact__num">{{ missionAuthor.years_experience }}</span>
                                    <span class="pvhx-author-fact__label">Jahre Berufserfahrung</span>
                                </div>
                                <div v-if="missionAuthor.region" class="pvhx-author-fact">
                                    <span class="pvhx-author-fact__num">-</span>
                                    <span class="pvhx-author-fact__label">{{ missionAuthor.region }}</span>
                                </div>
                            </div>
                            <div v-if="missionAuthor.linkedin_url || missionAuthor.xing_url || missionAuthor.email" class="pvhx-author-social">
                                <a v-if="missionAuthor.linkedin_url" :href="missionAuthor.linkedin_url" target="_blank" rel="noopener nofollow" class="pvhx-author-social__link">LinkedIn</a>
                                <a v-if="missionAuthor.xing_url" :href="missionAuthor.xing_url" target="_blank" rel="noopener nofollow" class="pvhx-author-social__link">XING</a>
                                <a v-if="missionAuthor.email" :href="`mailto:${missionAuthor.email}`" class="pvhx-author-social__link">E-Mail</a>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- CTA -->
                <section class="pvhx-cta">
                    <div class="pvhx-wrap pvhx-cta__inner">
                        <h2 class="pvhx-cta__title">Haben Sie Fragen?</h2>
                        <p class="pvhx-cta__sub">Kontaktieren Sie uns - wir beraten Sie gerne kostenlos und unverbindlich.</p>
                        <div class="pvhx-cta__funnel">
                            <LeadFunnel :domain="domain" :service="service" :design-variant="designVariant" bare :dark-mode="false" />
                        </div>
                    </div>
                </section>
            </div>
        </VariantPageWrapper>
    </AppLayout>
</template>

<style scoped>
/* pvhx-about-* / shared pvhx-* namespace, matched to PvKostenrechnerChrome /
   Home.vue (paper #f4f1ea, ink #1c1917, amber accent, Lora + Inter). */
.pvhx-about {
    --pvhx-ink: #1c1917;
    --pvhx-ink-2: #44403c;
    --pvhx-mute: #78716c;
    --pvhx-page: #f4f1ea;
    --pvhx-surface: #ffffff;
    --pvhx-line: #e4d8c6;
    --pvhx-line-2: #cfc0a8;
    background: var(--pvhx-page);
    color: var(--pvhx-ink);
    font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
    font-size: 16px;
    line-height: 1.55;
    -webkit-font-smoothing: antialiased;
}

.pvhx-about h1,
.pvhx-about h2,
.pvhx-about h3 {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    letter-spacing: -0.015em;
}

.pvhx-wrap {
    max-width: 1240px;
    margin: 0 auto;
    padding: 0 32px;
}

.pvhx-wrap--narrow {
    max-width: 960px;
}

@media (max-width: 600px) {
    .pvhx-wrap { padding: 0 20px; }
}

.pvhx-eyebrow {
    font-size: 11px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--pvhx-accent, #ea580c);
    font-weight: 600;
    margin: 0 0 18px;
}

.pvhx-h2 {
    font-size: clamp(26px, 3.4vw, 38px);
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--pvhx-ink);
    margin: 0 0 20px;
}

.pvhx-h2--center {
    text-align: center;
    margin-bottom: 40px;
}

.pvhx-h3 {
    font-size: 20px;
    line-height: 1.25;
    color: var(--pvhx-ink);
    margin: 0 0 10px;
}

.pvhx-p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--pvhx-ink-2);
    margin: 0 0 16px;
}
.pvhx-p:last-child { margin-bottom: 0; }

.pvhx-lead {
    font-size: 18px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    max-width: 58ch;
    margin: 0;
}

/* Hero */
.pvhx-about-hero {
    padding: 88px 0 56px;
    border-bottom: 1px solid var(--pvhx-line);
    position: relative;
}
.pvhx-about-hero::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(circle at 90% -20%, rgba(234, 88, 12, 0.12), transparent 45%);
}
.pvhx-about-h1 {
    position: relative;
    font-size: clamp(38px, 5.4vw, 64px);
    line-height: 1.0;
    letter-spacing: -0.025em;
    color: var(--pvhx-ink);
    margin: 0 0 20px;
    max-width: 18ch;
}

/* Sections */
.pvhx-section {
    padding: 72px 0;
    border-bottom: 1px solid var(--pvhx-line);
}
.pvhx-section--tight {
    padding: 64px 0;
}

/* Mission / Regional split */
.pvhx-mission,
.pvhx-regional {
    display: flex;
    flex-direction: column;
    gap: 32px;
    align-items: stretch;
}

@media (min-width: 960px) {
    .pvhx-mission,
    .pvhx-regional {
        flex-direction: row;
        gap: 56px;
        align-items: center;
    }
    .pvhx-mission__content,
    .pvhx-mission__media,
    .pvhx-regional__content,
    .pvhx-regional__media {
        width: 50%;
    }
}

.pvhx-mission__media,
.pvhx-regional__media {
    min-height: 300px;
    background: var(--pvhx-surface);
    background-size: cover;
    background-position: center;
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
}

.pvhx-mission__trust {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 22px;
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid var(--pvhx-line);
}

.pvhx-trust-item {
    font-size: 13px;
    font-weight: 600;
    color: var(--pvhx-ink);
}

/* Stats */
.pvhx-stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
}

@media (min-width: 960px) {
    .pvhx-stats { grid-template-columns: repeat(4, 1fr); }
}

.pvhx-stat {
    text-align: center;
    padding: 30px 18px;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-top: 3px solid var(--pvhx-accent, #ea580c);
    border-radius: 6px;
}

.pvhx-stat__value {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 36px;
    font-weight: 500;
    color: var(--pvhx-accent, #ea580c);
    line-height: 1;
    margin-bottom: 8px;
}

.pvhx-stat__label {
    font-size: 13px;
    color: var(--pvhx-mute);
    line-height: 1.4;
}

/* Values */
.pvhx-values {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
}

@media (min-width: 768px) {
    .pvhx-values { grid-template-columns: repeat(3, 1fr); }
}

.pvhx-value {
    padding: 30px 26px;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
    text-align: center;
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.pvhx-value:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 40px rgba(28, 22, 16, 0.10);
    border-color: var(--pvhx-line-2);
}

.pvhx-value__num {
    width: 46px;
    height: 46px;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--pvhx-accent, #ea580c);
    color: #fff;
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 18px;
}

/* Regional callout */
.pvhx-callout {
    margin-top: 24px;
    padding: 22px;
    background: rgba(234, 88, 12, 0.12);
    border: 1px solid var(--pvhx-line);
    border-left: 3px solid var(--pvhx-accent, #ea580c);
    border-radius: 0 6px 6px 0;
}

.pvhx-callout__label {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 15px;
    color: var(--pvhx-accent, #ea580c);
    margin: 0 0 6px;
}

.pvhx-callout__text {
    font-size: 14px;
    line-height: 1.6;
    color: var(--pvhx-ink-2);
    margin: 0;
}

/* Author */
.pvhx-author-accent { color: var(--pvhx-accent, #ea580c); }

.pvhx-author-headline {
    font-size: 17px;
    color: var(--pvhx-ink-2);
    margin: 0 0 32px;
}

.pvhx-author-grid {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 32px;
    align-items: start;
    margin-bottom: 36px;
}

@media (max-width: 640px) {
    .pvhx-author-grid { grid-template-columns: 1fr; gap: 20px; }
}

.pvhx-author-photo {
    width: 140px;
    height: 140px;
    border-radius: 50%;
    overflow: hidden;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
}

.pvhx-author-photo__fallback {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 44px;
    color: var(--pvhx-accent, #ea580c);
}

.pvhx-author-meta {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 32px;
    padding-top: 28px;
    border-top: 1px solid var(--pvhx-line);
}

@media (max-width: 640px) {
    .pvhx-author-meta { grid-template-columns: 1fr; gap: 20px; }
}

.pvhx-author-creds__label {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--pvhx-mute);
    margin: 0 0 10px;
}

.pvhx-author-creds__list {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: 14px;
    color: var(--pvhx-ink-2);
}

.pvhx-author-creds__list li {
    padding: 4px 0;
    border-bottom: 1px dotted var(--pvhx-line);
}
.pvhx-author-creds__list li:last-child { border-bottom: none; }

.pvhx-author-facts {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.pvhx-author-fact {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.pvhx-author-fact__num {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-size: 28px;
    color: var(--pvhx-accent, #ea580c);
    line-height: 1;
}

.pvhx-author-fact__label {
    font-size: 12px;
    color: var(--pvhx-mute);
    margin-top: 4px;
}

.pvhx-author-social {
    grid-column: 1 / -1;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    padding-top: 8px;
}

.pvhx-author-social__link {
    font-size: 13px;
    text-decoration: none;
    color: var(--pvhx-accent, #ea580c);
    border: 1px solid var(--pvhx-line);
    padding: 7px 14px;
    border-radius: 4px;
    transition: background 0.15s ease;
}

.pvhx-author-social__link:hover {
    background: rgba(234, 88, 12, 0.12);
}

/* CTA */
.pvhx-cta {
    background: #1c1917;
    color: #f4f1ea;
}

.pvhx-cta__inner {
    padding: 72px 32px;
    text-align: center;
}

.pvhx-cta__title {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    font-weight: 500;
    font-size: clamp(28px, 3.6vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.025em;
    color: #f4f1ea;
    margin: 0 0 14px;
}

.pvhx-cta__sub {
    font-size: 16px;
    line-height: 1.6;
    color: rgba(251, 246, 236, 0.7);
    max-width: 56ch;
    margin: 0 auto 32px;
}

.pvhx-cta__funnel {
    max-width: 640px;
    margin: 0 auto;
    background: var(--pvhx-surface);
    border: 1px solid var(--pvhx-line);
    border-radius: 6px;
    padding: 32px;
    text-align: left;
}

@media (max-width: 600px) {
    .pvhx-about-hero { padding: 56px 0 40px; }
    .pvhx-section, .pvhx-section--tight { padding: 48px 0; }
    .pvhx-cta__inner { padding: 56px 20px; }
    .pvhx-cta__funnel { padding: 22px; }
}

@media (prefers-reduced-motion: reduce) {
    .pvhx-about *, .pvhx-about *::before, .pvhx-about *::after {
        transition: none !important;
        animation: none !important;
    }
}
</style>
